import { useEffect, useState } from "react";
import apiAgent from "../api/apiAgent";
import { useNavigate } from "react-router";
import { tMsg } from "../components/ToastNotifications";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { generalDataAtom, loginStatusAtom, userAtom } from "../atoms";
import { Link } from "react-router-dom";
import _ from "lodash";

interface ILoginData {
  name: string;
  password: string;
}

const defaultLoginData = {
  name: "",
  password: "",
};

// TODO: Change these for a production CMS
const loginLogo = "/assets/nma_logo.png";
const loginText = "Defining Moments CMS Login";

const checkUserResponseData = (data: any) =>
  ["username", "user_role", "mfa_enabled", "mfa_verified"].every(
    (k) => k in data
  );

const Authenticate = (): JSX.Element => {
  const navigate = useNavigate();
  const generalData = useAtomValue(generalDataAtom);
  const [data, setData] = useState<ILoginData>(defaultLoginData);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [loginStatus, setLoginStatus] = useAtom(loginStatusAtom);
  const setUser = useSetAtom(userAtom);

  useEffect(() => {
    validateAuthentication();
  }, []);

  const validateAuthentication = async () => {
    // If already logged in, navigate to home page
    if (loginStatus === true) {
      navigate("/");
      return;
    }
    // If already logged in, navigate to home page
    try {
      const response = await apiAgent.Auth.validate();
      if (response.status === 200) {
        setLoginStatus(true);
        navigate("/");
      }
    } catch (err) {
      // console.log(err);
      // console.log("No valid login status");
    }
  };

  const authenticate = async () => {
    try {
      // authenticate here

      const params = {
        name: data.name,
        password: data.password,
      };

      const response = await apiAgent.Auth.login(params);
      const userData = response.data;
      if (response.status === 200) {
        // Check if user data is correct - if so set user values
        if (checkUserResponseData(userData)) setUser(userData);
        else {
          // Log error
          tMsg.warning("There was an issue pulling user data");
          console.log("User response data was invalid");
        }

        if (userData.mfa_enabled && generalData.mfa_enabled) {
          navigate("/mfa_auth");
        } else {
          navigate("/");
          tMsg.success("Login successful");
          setLoginStatus(true);
        }
      }
    } catch (err: any) {
      console.log("Authentication error:", err);
      // setErrorMessage("Invalid login details");
      setErrorMessage(
        _.capitalize(
          _.trimEnd((err?.response?.data || "Error Logging in").toString())
        )
      );
      setData(defaultLoginData);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setData({
      ...data,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !data.name ||
      data.name.length < 1 ||
      !data.password ||
      data.password.length < 1
    ) {
      setErrorMessage("Valid username & password required");
      return;
    }
    authenticate();
  };

  return (
    <div
      className="w-full h-full grid place-content-center bg-cover gap-4"
      style={{ backgroundImage: "url(/assets/nma.jpeg)" }}
    >
      <div className="flex justify-center w-full">
        <img src={loginLogo} className="w-[250px]" />
      </div>
      <div className="w-96 py-8 space-y-2">
        <h1 className="text-white text-center w-full text-xl font-bold mb-3">
          {loginText}
        </h1>
        <div className="h-5 w-full text-center text-red-500 font-medium text-sm mb-1">
          {errorMessage || ""}
        </div>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col space-y-4 text-black">
            <input
              className="p-2 bg-gray-300"
              name="name"
              type="text"
              placeholder="Username"
              value={data.name}
              onChange={handleChange}
            />
            <input
              className="p-2 bg-gray-300"
              name="password"
              type="password"
              placeholder="Password"
              value={data.password}
              onChange={handleChange}
            />
            <button
              type="submit"
              className="bg-primaryColor text-white p-2 transition hover:bg-white hover:text-primaryColor border border-primaryColor"
            >
              Login
            </button>
          </div>
        </form>
        <div className="text-white opacity-80 hover:opacity-100 text-sm font-bold transition text-center pt-2">
          <Link to="/request_reset">Forgot your password?</Link>
        </div>
      </div>
    </div>
  );
};

export default Authenticate;
